import * as XLSX from "xlsx-js-style";

/**
 * PK Shipments' Export (user, 2026-10-30: "export ... like DHL module"):
 * the old Manufacturing -> DHL Tracking sheet, same sections, columns and
 * look, for the PK-DHL / PK-UPS rows.
 *
 * Sections, by the row's location and linked customer:
 *   Shipped to Krown Warehouse   location Krown, customer Krown
 *   Shipped to Krown Customers   location Krown, any other customer
 *   Shipped to Nova Customers    location Nova
 * A row in none of them (no location, another location) goes under
 * "Other" -- the DHL sheet left those out.
 */

const HEADERS = ["Sr.", "Dispatch Date", "Tracking Number", "Tracking Details", "C-ID", "Customer", "Location", "Status"];
const LAST = HEADERS.length - 1;
const CENTRED = new Set([0, 4, 6, 7]);

const black = (style) => ({ style, color: { rgb: "000000" } });
const titleStyle = {
  font: { bold: true, sz: 14, color: { rgb: "000000" } },
  fill: { fgColor: { rgb: "FFFF00" } },
  alignment: { horizontal: "center" },
  border: { top: black("medium"), bottom: black("medium"), left: black("medium"), right: black("medium") },
};
const headerStyle = {
  font: { bold: true },
  fill: { fgColor: { rgb: "D9D9D9" } },
};

// Thin borders, medium on the table's outer edges.
const cellBorder = (col, lastRow) => ({
  top: black("thin"),
  bottom: black(lastRow ? "medium" : "thin"),
  left: black(col === 0 ? "medium" : "thin"),
  right: black(col === LAST ? "medium" : "thin"),
});

// A styled cell. The type is set: a styled cell without one is skipped when
// the sheet is read back.
const cell = (v, s) => ({ v, t: typeof v === "number" ? "n" : "s", s });

const has = (value, word) => String(value || "").toLowerCase().includes(word);

export const sectionsOf = (rows) => {
  const krown = (r) => has(r.location?.locationName, "krown");
  const nova = (r) => has(r.location?.locationName, "nova");
  const krownCustomer = (r) => has(r.customer?.customerName, "krown");
  return [
    ["Shipped to Krown Warehouse", rows.filter((r) => krown(r) && krownCustomer(r))],
    ["Shipped to Krown Customers", rows.filter((r) => krown(r) && !krownCustomer(r))],
    ["Shipped to Nova Customers", rows.filter((r) => !krown(r) && nova(r))],
    ["Other", rows.filter((r) => !krown(r) && !nova(r))],
  ].filter(([, list]) => list.length);
};

const rowValues = (r, i) => [
  i + 1,
  r.shipDate ? new Date(r.shipDate).toLocaleDateString() : "",
  r.trackingNumber || "",
  r.trackingInfo || "",
  r.customerId || "",
  r.customer?.customerName || "",
  r.location?.locationName || (r.locationId ? `Location #${r.locationId}` : ""),
  r.status === "delivered" ? "Delivered" : "In-Transit",
];

export default function exportPkShipments(rows) {
  const sheet = [];
  const merges = [];
  sectionsOf(rows).forEach(([title, list], s) => {
    if (s > 0) sheet.push([""]);
    merges.push({ s: { r: sheet.length, c: 0 }, e: { r: sheet.length, c: LAST } });
    sheet.push(HEADERS.map((_, c) => cell(c ? "" : title, titleStyle)));
    sheet.push(
      HEADERS.map((h, c) =>
        cell(h, {
          ...headerStyle,
          ...(CENTRED.has(c) ? { alignment: { horizontal: "center" } } : {}),
          border: {
            bottom: black("thin"),
            ...(c === 0 ? { left: black("medium") } : {}),
            ...(c === LAST ? { right: black("medium") } : {}),
          },
        }),
      ),
    );
    list.forEach((r, i) =>
      sheet.push(
        rowValues(r, i).map((v, c) =>
          cell(v, {
            ...(CENTRED.has(c) ? { alignment: { horizontal: "center" } } : {}),
            border: cellBorder(c, i === list.length - 1),
          }),
        ),
      ),
    );
  });

  const worksheet = XLSX.utils.aoa_to_sheet(sheet);
  worksheet["!merges"] = merges;
  worksheet["!cols"] = [5, 15, 20, 45, 10, 25, 20, 15].map((wch) => ({ wch }));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "PK Shipments");
  XLSX.writeFile(workbook, `pk-shipments-${new Date().toISOString().slice(0, 10)}.xlsx`);
}
